import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { Form, Formik } from 'formik';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import ExerciseLogoIcon from 'src/assets/img/exercise-logo-icon.svg';
import { Alert } from 'src/components/Alert';
import { BackgroundGradient } from 'src/components/BackgroundGradient';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { Container } from 'src/components/Container';
import { DrawerLayout } from 'src/components/Drawer';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { SiteFooter } from 'src/components/SiteFooter';
import { Stack } from 'src/components/Stack';
import { InputAdornment } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { styled } from 'src/components/muiTheme/utils';
import { Collapse } from 'src/components/transitions/Collapse';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { TwoFactorRequired, User } from 'src/models/user/_types';
import {
  resendTwoFactorCode,
  signInUser,
  signOutUser,
  verifyTwoFactorCode,
} from 'src/models/user/user';
import { isAdmin } from 'src/models/user/utils';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import * as Yup from 'yup';

import { TwoFactorPrompt } from './TwoFactorPrompt';


const BackgroundWrapper = styled('div')(() => ({
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '100%',
  height: '100%',
  zIndex: 0,
}));

const PageWrapper = styled('div')(() => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    refreshSessionAndUser,
    user,
    userLoading,
    session,
    sessionLoading,
    isDarkmode,
    disableDarkmode,
    enableDarkmode,
  } = useOptionalSession();
  const { t } = useTranslation('translation', { keyPrefix: 'login' });

  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorRequired | null>(
    null
  );
  const [twoFactorVerifying, setTwoFactorVerifying] = useState(false);
  const [twoFactorResending, setTwoFactorResending] = useState(false);
  const [twoFactorSuccessMessage, setTwoFactorSuccessMessage] = useState('');

  const loginFormik = {
    initialValues: {
      email: new URLSearchParams(location.search).get('email') || '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required(t('email-required'))
        .email(t('invalid-email'))
        .transform((value) => value.replace(/\s+/g, '')),
      password: Yup.string().required(t('password-required')),
    }),
    onSubmit: async (values) => {
      try {
        setError('');
        const result = await signInUser(
          values.email,
          values.password,
          rememberMe
        );

        // Check if 2FA is required
        if ('twoFactorRequired' in result && result.twoFactorRequired) {
          setTwoFactorData(result);
          return;
        }

        // Normal login flow - result is a User object at this point
        const signedInUser = result as User;
        await refreshSessionAndUser();

        if (
          session?.id &&
          isAdmin(signedInUser) &&
          (!redirectUrl || !redirectUrl.includes('/login'))
        ) {
          await signOutUser();
          window.location.href = externalLinks.platformAdmin(session?.id);
        } else {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            navigate(redirectPath, { replace: true });
          }
        }
      } catch (e) {
        const error = e as any;
        if (error?.response?.data?.message) {
          setError(error?.response?.data?.message);
        } else {
          setError(t('sign-in-error'));
        }
      }
    },
  };

  const handleTwoFactorVerify = async (otpCode: string) => {
    if (!twoFactorData) return;

    setTwoFactorVerifying(true);
    try {
      const signedInUser = await verifyTwoFactorCode(
        twoFactorData.userId,
        otpCode
      );
      await refreshSessionAndUser();

      // Clear 2FA state
      setTwoFactorData(null);
      setError('');

      // Continue with normal login flow
      if (
        session?.id &&
        isAdmin(signedInUser) &&
        (!redirectUrl || !redirectUrl.includes('/login'))
      ) {
        await signOutUser();
        window.location.href = externalLinks.platformAdmin(session?.id);
      } else {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          navigate(redirectPath, { replace: true });
        }
      }
    } catch (e) {
      const error = e as any;
      setError(
        error?.response?.data?.message ||
          'Verification failed. Please try again.'
      );
    } finally {
      setTwoFactorVerifying(false);
    }
  };

  const handleTwoFactorResend = async () => {
    if (!twoFactorData) return;

    setTwoFactorResending(true);
    setTwoFactorSuccessMessage('');
    try {
      await resendTwoFactorCode(twoFactorData.userId);
      setTwoFactorSuccessMessage('Verification email has been resent!');
      setError('');
    } catch (e) {
      const error = e as any;
      setError(
        error?.response?.data?.message ||
          'Failed to resend code. Please try again.'
      );
    } finally {
      setTwoFactorResending(false);
    }
  };

  useEffect(() => {
    if (!sessionLoading) {
      const state = location.state as {
        unauthorizedMessage?: string;
        from?: string;
      };
      const searchParams = new URLSearchParams(location.search);
      const redirectTo = searchParams.get('redirect_to');

      if (state?.unauthorizedMessage) {
        setError(state.unauthorizedMessage);
      }

      // Prioritize redirect_to query param over from in location state
      if (redirectTo) {
        setRedirectUrl(redirectTo);
      } else if (state?.from) {
        setRedirectPath(state.from);
      } else if (!session?.id) {
        setRedirectUrl('/dashboard/');
      }
    }

  }, [location, navigate, session, sessionLoading]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (!userLoading && !sessionLoading && user?.id && (redirectPath || redirectUrl)) {
      timeoutId = setTimeout(() => {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          navigate(redirectPath, { replace: true });
        }
      }, 2000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, userLoading, sessionLoading, navigate, redirectPath, redirectUrl]);

  useLayoutEffect(() => {
    disableDarkmode();
    return () => {
      enableDarkmode();
    };
  }, [disableDarkmode, enableDarkmode]);

  const darkMode = isDarkmode();
  const logoUrl =
    darkMode && session?.darkmodeLogoUrl
      ? session.darkmodeLogoUrl
      : session?.logoUrl || ExerciseLogoIcon;

  return (
    <>
      <Meta title={t('sign-in')} brand={session?.brand} />
      <DrawerLayout drawerOpen={false}>
        <BackgroundWrapper>
          <BackgroundGradient color={error ? 'error' : 'primary'} />
        </BackgroundWrapper>

        <PageWrapper>
          <Box sx={{ flex: '1 0 auto', pb: 5 }}>
            <Container
              maxWidth="lg"
              sx={(theme) => ({
                [theme.breakpoints.down('md')]: {
                  mb: 4,
                  mt: 4,
                },
                mb: 6,
                mt: 8,
                position: 'relative',
              })}
            >
              <Stack
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {!sessionLoading && session && (
                  <img
                    src={logoUrl}
                    style={{ maxHeight: '80px' }}
                    alt={`${session?.brand || 'Exercise'} Logo`}
                  />
                )}
              </Stack>
            </Container>
            <Container maxWidth="sm" sx={{ position: 'relative' }}>
              {twoFactorData ? (
                <TwoFactorPrompt
                  userId={twoFactorData.userId}
                  method={twoFactorData.method}
                  message={twoFactorData.message}
                  onVerify={handleTwoFactorVerify}
                  onResend={
                    twoFactorData.method === 'email'
                      ? handleTwoFactorResend
                      : undefined
                  }
                  isVerifying={twoFactorVerifying}
                  isResending={twoFactorResending}
                  error={error}
                  successMessage={twoFactorSuccessMessage}
                />
              ) : (
                <Card
                  sx={{ p: 2.5, bgcolor: 'transparent', boxShadow: 'none' }}
                >
                  <Stack
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h4" mb={1}>
                      {t('sign-in')}
                    </Typography>
                  </Stack>

                  <Collapse in={!!error} collapsedSize={0}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  </Collapse>

                  <Formik enableReinitialize={true} {...loginFormik}>
                    {({ isSubmitting, isValid, dirty }) => (
                      <Form>
                        <FormTextField
                          sx={{ mt: 0 }}
                          label={t('email')}
                          name="email"
                        />
                        <FormTextField
                          sx={{ mt: 3 }}
                          label={t('password')}
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                <Icon
                                  size="small"
                                  icon={
                                    showPassword
                                      ? faEyeSlash
                                      : faEye
                                  }
                                />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mt: 3 }}
                        >
                          <Checkbox
                            label={t('remember-me')}
                            checked={rememberMe}
                            onChange={(event) =>
                              setRememberMe(event.target.checked)
                            }
                          />
                          <Link
                            variant="body2"
                            component={RouterLink}
                            to={`/reset-password/`}
                          >
                            {t('forgot-password')}
                          </Link>
                        </Stack>

                        <Button
                          variant="contained"
                          sx={{ mt: 3 }}
                          disabled={isSubmitting || !isValid || !dirty}
                          loading={isSubmitting}
                          type="submit"
                          fullWidth
                        >
                          {t('sign-in')}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </Card>
              )}

              {(!session?.id || session?.freeSignups) && (
                <Typography
                  sx={{ mt: 5, position: 'relative' }}
                  align="center"
                  variant="body1"
                  color="text.secondary"
                >
                  {t('no-account')}
                  <br />
                  <Link component={RouterLink} to={`/sign-up`}>
                    {t('sign-up')}
                  </Link>
                </Typography>
              )}

              {session?.id && !session?.hideLandingPage && (
                <Typography
                  sx={{ mt: 5, position: 'relative' }}
                  align="center"
                  variant="body1"
                  color="text.secondary"
                >
                  {t('no-brand-account', {
                    article: /^[aeiou]/i.test(session.brand || '') ? 'an' : 'a',
                    brand: session.brand,
                  })}
                  <br />
                  <Link
                    component="a"
                    href={
                      session?.alternateLanding ||
                      externalLinks.landingPage(session.trainerId)
                    }
                  >
                    {t('click-here')}
                  </Link>{' '}
                  {t('to-see-options')}
                </Typography>
              )}
            </Container>
          </Box>
          <SiteFooter />
        </PageWrapper>
      </DrawerLayout>
    </>
  );
};
